import { useEffect, useRef, useState } from "react";
import {
  CellWrapper,
  useCellContext,
  useDoubleTouch,
  isValidKey,
} from "@silevis/reactgrid";

import Point from "./Point";
import ShiftPopover from "./components/ShiftPopover";
import useFixedPopover from "./useFixedPopover";
import { cn } from "tools/ui";

function CellData({
  text: initialText,
  onTextChanged,
  style,
  onDoubleClick,
  onSelectCell,
  hasEvent,
  events,
  shifts,
  isSunday,
  weeklyTotal,
  weekHoursLimit,
  isBlocked = false,
}) {
  const ctx = useCellContext();
  const targetInputRef = useRef(null);
  const [isEditMode, setEditMode] = useState(false);
  const [currentValue, setCurrentValue] = useState(initialText || "");
  const escapePressedRef = useRef(false);
  const committedRef = useRef(false);
  const { handleDoubleTouch } = useDoubleTouch(ctx, setEditMode);
  const {
    triggerRef: shiftRef,
    popoverPos: shiftPopover,
    showPopover: showShiftPopover,
    hidePopover: hideShiftPopover,
  } = useFixedPopover();

  useEffect(() => {
    setCurrentValue(initialText);
  }, [initialText]);

  function onPointerDown(e) {
    e.stopPropagation();
  }

  function onDBClick() {
    if (isBlocked) return;
    if (ctx.isFocused) {
      if (onDoubleClick) {
        onDoubleClick();
      }
    }
  }

  return (
    <CellWrapper
      onStringValueRequested={() => initialText}
      onStringValueReceived={(v) => {
        if (isBlocked) return;
        if (isEditMode) {
          if (committedRef.current) return;
          committedRef.current = true;
        }
        onTextChanged?.(v);
      }}
      onTouchEnd={isBlocked ? undefined : handleDoubleTouch}
      style={style}
      onDoubleClick={onDBClick}
      onFocus={onSelectCell}
      onKeyDown={(e) => {
        if (isBlocked) {
          e.stopPropagation();
          return;
        }
        if (!isEditMode && isValidKey(e, [])) {
          e.stopPropagation();
          committedRef.current = false;
          setCurrentValue("");
          setEditMode(true);
        } else if (
          !isEditMode &&
          !ctx.isSelected &&
          (e.key === "Enter" || e.key === "F2")
        ) {
          e.stopPropagation();
          committedRef.current = false;
          setCurrentValue(initialText ? `${initialText}-` : "");
          setEditMode(true);
        }
      }}
    >
      {isEditMode ? (
        <input
          className="rg-input"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.currentTarget.value)}
          onFocus={() => {
            committedRef.current = false;
          }}
          onBlur={(e) => {
            if (committedRef.current) {
              setEditMode(false);
              return;
            }
            if (!escapePressedRef.current) {
              committedRef.current = true;
              onTextChanged?.(e.currentTarget.value);
            }
            setEditMode(false);
            if (escapePressedRef.current) {
              escapePressedRef.current = false;
            }
          }}
          onCut={(e) => e.stopPropagation()}
          onCopy={(e) => e.stopPropagation()}
          onPaste={(e) => e.stopPropagation()}
          onPointerDown={onPointerDown}
          onKeyDown={(e) => {
            const controlKeys = ["Escape", "Enter", "Tab"];
            if (!controlKeys.includes(e.key)) {
              e.stopPropagation();
            }
            if (e.key === "Escape") {
              escapePressedRef.current = true;
              committedRef.current = true;
              setEditMode(false);
            } else if (e.key === "Enter") {
              setEditMode(false);
            }
          }}
          ref={targetInputRef}
          autoFocus
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-0.5 min-h-[36px] py-0.5">
          {isBlocked ? null : shifts?.length ? (
            <span
              ref={shiftRef}
              onMouseEnter={showShiftPopover}
              onMouseLeave={hideShiftPopover}
            >
              {initialText}
            </span>
          ) : (
            <span>{initialText}</span>
          )}
          {hasEvent && (
            <Point
              color="bg-cyan-500"
              position="animate-pulse"
              events={events}
              popover
            />
          )}
          {isSunday && weeklyTotal !== undefined && weeklyTotal !== null && (
            <span
              className={cn(
                "text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full leading-none select-none pointer-events-none transition-all mt-0.5 shadow-2xs backdrop-blur-xs",
                weekHoursLimit && weeklyTotal > weekHoursLimit
                  ? "bg-amber-100/95 text-amber-900 border border-amber-300/90 font-bold"
                  : weekHoursLimit && weeklyTotal === weekHoursLimit
                  ? "bg-emerald-100/95 text-emerald-900 border border-emerald-300/90 font-bold"
                  : "bg-slate-100/95 text-slate-700 border border-slate-300/80"
              )}
            >
              {weeklyTotal}h
            </span>
          )}
        </div>
      )}
      {shiftPopover && shifts?.length > 0 && (
        <ShiftPopover
          pos={shiftPopover}
          shifts={shifts}
          onMouseEnter={showShiftPopover}
          onMouseLeave={hideShiftPopover}
        />
      )}
    </CellWrapper>
  );
}

export default CellData;
